import graphql from 'babel-plugin-relay/macro';
import React from 'react';
import {usePaginationFragment, useMutation} from 'react-relay/hooks';
import ConnectionHandler from 'relay-connection-handler-plus';
import useFilters from 'src/hooks/useFilters';
import {showEventNotification} from 'src/utils/notifications';

import Table from '../../../components/table';
import DateCell from '../../../components/table/cell-types/DateCell';
import SecondaryTextCell from '../../../components/table/cell-types/SecondaryTextCell';
import {listPagevalveModelsQuery} from './__generated__/listPagevalveModelsQuery.graphql';
import {AssetModelsOrder} from './__generated__/ValveModelsPaginationQuery.graphql';
import {ValveModelsTable_valveModels$key} from './__generated__/ValveModelsTable_valveModels.graphql';
import {ValveModelsTableDeleteValveModelMutation} from './__generated__/ValveModelsTableDeleteValveModelMutation.graphql';
import TableFilters from './TableFilters';

type Props = {
  valveModels: ValveModelsTable_valveModels$key;
};

type Filters = {
  name?: string;
  orderBy?: AssetModelsOrder;
};

function ValveModelsTable(props: Props) {
  const {data, loadNext, hasNext, refetch} = usePaginationFragment<
    listPagevalveModelsQuery,
    ValveModelsTable_valveModels$key
  >(
    graphql`
      fragment ValveModelsTable_valveModels on Query
        @argumentDefinitions(
          first: {type: "Int!"}
          after: {type: "String"}
          name: {type: "String"}
          orderBy: {type: "AssetModelsOrder"}
        )
        @refetchable(queryName: "ValveModelsPaginationQuery") {
        valveModels(first: $first, after: $after, orderBy: $orderBy, name: $name)
          @connection(key: "ValveModelsList_valveModels") {
          totalCount
          pageInfo {
            hasPreviousPage
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              createdAt
              name
              family {
                id
                name
              }
              manufacturer {
                id
                name
              }
            }
          }
        }
      }
    `,
    props.valveModels,
  );

  const [commit] = useMutation<ValveModelsTableDeleteValveModelMutation>(deleteValveModelMutation);
  const {addFilters, setFilters, isTransitioning} = useFilters<Filters>({}, refetch);

  return (
    <Table
      filters={() => (
        <TableFilters onApplyFilters={handleOnApplyFilter} onResetFilters={handleOnResetFilter} />
      )}
      actions={{show: false, delete: true, edit: true, create: true}}
      totalDataLength={data.valveModels.totalCount}
      columns={columns}
      onPaginate={() => hasNext && loadNext(50)}
      onSort={handleOnSort}
      onDelete={handleDelete}
      isTransitioning={isTransitioning}
      data={data.valveModels.edges}
    />
  );

  function handleDelete(id: string) {
    commit({
      variables: {input: {valveModelId: id}},
      onCompleted: payload => {
        const entry = payload.deleteValveModel?.valveModel;

        if (entry) {
          showEventNotification('removed', 'Model', entry.name);
        }
      },
      updater: store => {
        const root = store.getRoot();

        const connections = ConnectionHandler.getConnections(root, 'ValveModelsList_valveModels');

        if (!connections) {
          return;
        }

        connections.forEach(connection => {
          ConnectionHandler.deleteNode(connection, id);
        });
      },
    });
  }

  function handleOnApplyFilter(filters: {text: string}) {
    addFilters({name: filters.text});
  }

  function handleOnResetFilter() {
    setFilters({});
  }

  async function handleOnSort(sortByConfig: any[]) {
    if (!sortByConfig.length) {
      addFilters({orderBy: undefined});

      return;
    }

    const orderByDirection = sortByConfig[0].desc ? 'DESC' : 'ASC';
    const orderByField = sortByConfig[0].id.toUpperCase();

    addFilters({orderBy: {direction: orderByDirection, field: orderByField}});
  }
}

export default ValveModelsTable;

const deleteValveModelMutation = graphql`
  mutation ValveModelsTableDeleteValveModelMutation($input: DeleteValveModelInput!) {
    deleteValveModel(input: $input) {
      valveModel {
        id
        name
      }
      errors {
        code
        message
      }
    }
  }
`;

const columns = [
  {
    Header: 'Name',
    accessor: 'node.name',
    id: 'name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Created On',
    accessor: 'node.createdAt',
    id: 'created_at',
    Cell({cell: {value}}: any) {
      return <DateCell value={value} />;
    },
  },
  {
    Header: 'Manufacturer',
    accessor: 'node.manufacturer.name',
    id: 'manufacturer_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Type',
    accessor: 'node.family.name',
    id: 'family_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
];
